/**
 * Each person's settings: who you are signed in as, which emails you get,
 * and the way out. The organization's single sign-on setup used to sit here
 * too, for its System Admin; it is withheld until single sign-on has been
 * validated against a real directory (`SSO_RELEASED_TO_CUSTOMERS`).
 *
 * Confirming which account and which organization someone is in is the
 * first question asked on every support call, so it is the first thing on
 * the page. The email choices are the second: every policy system a
 * hospital has used lets a person switch off the noisier messages, and a
 * product that will not is the one people filter to a folder.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { ROLE_DISPLAY_NAMES, hasAtLeast } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { MAIL_KINDS, MAIL_LABELS, useNotificationPrefs, useSaveNotificationPrefs, type MailKind } from './useNotificationPrefs';
import { MfaGate } from '../console/MfaGate';
import { SSO_RELEASED_TO_CUSTOMERS } from '../../auth/sso';
import { SsoSection } from '../console/SsoSection';
import { useUpdateMemberProfile } from '../admin/useMembers';
import { useAddressManagedBySso, useCancelEmailChange, usePendingEmail, useRequestEmailChange } from './useEmailChange';
import { EMAIL_MAX, PERSON_NAME_MAX } from '../../types/fieldLimits';
import { splitName } from '../../types/personName';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import { ThemeChoice } from '../../shared/ThemeChoice';

/**
 * Your own name, as the staff table and every acknowledgement record show
 * it. It used to be the part of your address before the @, with no way to
 * change it.
 */
function NameEditor({ current, orgId }: { current: string | null; orgId: string | null }) {
  const { refresh } = useAuth();
  const update = useUpdateMemberProfile(orgId);
  const [editing, setEditing] = useState(false);
  // Kept in parts, as the hospital's directory keeps them. The session only
  // carries the full name, so a best split seeds the fields to correct.
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  function start() {
    const parts = splitName(current);
    setFirst(parts.firstName); setLast(parts.lastName); setEditing(true);
  }
  const blank = !first.trim() && !last.trim();
  if (!editing) {
    return (
      <>
        {current ?? <span className={styles.absent}>Not set</span>}
        {orgId && (
          <>
            {' · '}
            <button type="button" className={styles.btn} onClick={start}>
              {current ? 'Change' : 'Set your name'}
            </button>
          </>
        )}
      </>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (blank) return;
        update.mutate({ firstName: first.trim(), lastName: last.trim() }, { onSuccess: async () => { await refresh(); setEditing(false); } });
      }}
      style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}
    >
      <div><label className={form.label} htmlFor="settings-first-name">First name</label>
      <input id="settings-first-name" className={form.input} style={{ maxWidth: 180 }} maxLength={PERSON_NAME_MAX} value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" autoFocus /></div>
      <div><label className={form.label} htmlFor="settings-last-name">Last name</label>
      <input id="settings-last-name" className={form.input} style={{ maxWidth: 180 }} maxLength={PERSON_NAME_MAX} value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" /></div>
      <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={update.isPending || blank}>{update.isPending ? 'Saving…' : 'Save'}</button>
      <button type="button" className={styles.btn} onClick={() => setEditing(false)}>Cancel</button>
      {update.error && <span role="alert" style={{ color: 'var(--danger-ink)' }}>{isCallFailure(update.error) ? update.error.message : 'Could not save.'}</span>}
    </form>
  );
}

/**
 * The address you sign in with. There is no password, so the address is the
 * account: a change is asked for here and confirmed from the NEW mailbox,
 * and until that link is opened nothing changes. An organization that
 * signs in through Microsoft has no editor; the address is the identity
 * provider's to change.
 */
function EmailEditor({ current, orgId, uid }: { current: string | null; orgId: string | null; uid: string | null }) {
  const pending = usePendingEmail(orgId, uid);
  const managed = useAddressManagedBySso(current);
  const request = useRequestEmailChange(orgId, uid);
  const cancel = useCancelEmailChange(orgId, uid);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const invalid = value.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  if (!current || !orgId) return <>{current ?? '—'}</>;

  if (pending.data && !editing) {
    return (
      <>
        {current}
        <p className={form.hint} style={{ margin: 'var(--sp-1) 0 0' }}>
          Changing to <strong>{pending.data.email}</strong>. Open the link we sent there to finish; until then this address still works.
          {' '}
          <button type="button" className={styles.linkBtn} onClick={() => request.mutate({ newEmail: pending.data!.email })} disabled={request.isPending}>Send the link again</button>
          {' · '}
          <button type="button" className={styles.linkBtn} onClick={() => cancel.mutate()} disabled={cancel.isPending}>Cancel the change</button>
        </p>
        {request.isSuccess && <p className={form.hint} role="status" style={{ margin: 'var(--sp-1) 0 0' }}>Sent again.</p>}
      </>
    );
  }

  if (!editing) {
    return (
      <>
        {current}
        {managed.data === true ? (
          <p className={form.hint} style={{ margin: 'var(--sp-1) 0 0' }}>Comes from your organization's sign-in provider. Ask IT to change it there.</p>
        ) : (
          <>
            {' · '}
            <button type="button" className={styles.btn} onClick={() => { setValue(''); setSentTo(null); setEditing(true); }} disabled={managed.isLoading}>
              Change email
            </button>
            {sentTo && <p className={form.hint} role="status" style={{ margin: 'var(--sp-1) 0 0' }}>We emailed a link to <strong>{sentTo}</strong>. Open it to finish the change.</p>}
          </>
        )}
      </>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const next = value.trim().toLowerCase();
        if (!next || invalid) return;
        request.mutate({ newEmail: next }, { onSuccess: (r) => { setSentTo(r.newEmail); setEditing(false); } });
      }}
      noValidate
    >
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="vh" htmlFor="settings-email">New email address</label>
        <input id="settings-email" type="email" className={form.input} style={{ maxWidth: 300 }} maxLength={EMAIL_MAX} value={value} onChange={(e) => setValue(e.target.value)} autoComplete="email" aria-invalid={invalid || undefined} aria-describedby="settings-email-hint" autoFocus />
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={request.isPending || !value.trim() || invalid}>{request.isPending ? 'Sending…' : 'Send confirmation link'}</button>
        <button type="button" className={styles.btn} onClick={() => setEditing(false)}>Cancel</button>
      </div>
      <p id="settings-email-hint" className={form.hint} style={{ margin: 'var(--sp-1) 0 0' }}>
        A link goes to the new address. Nothing changes until you open it, and every session signs out when it does.
      </p>
      {request.error && <p role="alert" className={form.hint} style={{ color: 'var(--danger-ink)', margin: 'var(--sp-1) 0 0' }}>{isCallFailure(request.error) ? request.error.message : 'Could not send the link.'}</p>}
    </form>
  );
}

export function SettingsPage() {
  const { session, signOut } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];
  const prefs = useNotificationPrefs(orgId, uid);
  const save = useSaveNotificationPrefs(orgId, uid);
  const [draft, setDraft] = useState<Record<MailKind, boolean> | null>(null);
  const [saved, setSaved] = useState(false);

  // Seeded once from what is saved, while rendering rather than in an effect
  // (which drew the page once empty, then again filled).
  if (prefs.data && draft === null) setDraft(prefs.data);

  const visibleKinds = MAIL_KINDS.filter((k) => {
    const need = MAIL_LABELS[k].forRole;
    return need === 'everyone' || hasAtLeast(roles, need);
  });
  const dirty = draft !== null && prefs.data !== undefined && visibleKinds.some((k) => draft[k] !== prefs.data![k]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft) return;
    const changes: Partial<Record<MailKind, boolean>> = {};
    for (const k of visibleKinds) if (prefs.data && draft[k] !== prefs.data[k]) changes[k] = draft[k];
    save.mutate(changes, { onSuccess: () => setSaved(true) });
  }

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
      </header>

      <div className={styles.body}>
        <section className={styles.section}>
          <dl className={styles.facts}>
            <div>
              <dt className={styles.factTerm}>Name</dt>
              <dd className={styles.factValue}>
                <NameEditor current={session?.displayName ?? null} orgId={session?.orgId ?? null} />
              </dd>
            </div>
            <div>
              <dt className={styles.factTerm}>Email</dt>
              <dd className={styles.factValue}>
                <EmailEditor current={session?.email ?? null} orgId={orgId} uid={uid} />
              </dd>
            </div>
            <div>
              <dt className={styles.factTerm}>Organization</dt>
              <dd className={styles.factValue}>
                {session?.orgId ?? '—'}
                {' · '}
                <Link to="/select-org" style={{ color: 'var(--accent-ink)', fontWeight: 600 }}>
                  Switch
                </Link>
              </dd>
            </div>
            <div>
              <dt className={styles.factTerm}>Roles</dt>
              <dd className={styles.factValue}>
                {session?.roles.length
                  ? session.roles.map((r) => ROLE_DISPLAY_NAMES[r]).join(', ')
                  : 'No role assigned'}
              </dd>
            </div>
          </dl>
        </section>

        <section className={styles.section} aria-labelledby="appearance-title">
          <h2 className={styles.sectionTitle} id="appearance-title">Appearance</h2>
          <p style={{ maxWidth: '68ch', lineHeight: 1.6, color: 'var(--ink-2)', marginTop: 0 }}>
            System follows this device, and changes with it when the device switches between light and
            dark. Your choice is kept with your account, so it is the same wherever you sign in. A
            document's own pages stay white, as they print.
          </p>
          <ThemeChoice />
        </section>

        <section className={styles.section} aria-labelledby="email-prefs-title">
          <h2 className={styles.sectionTitle} id="email-prefs-title">Email</h2>
          <p style={{ maxWidth: '68ch', lineHeight: 1.6, color: 'var(--ink-2)', marginTop: 0 }}>
            Which messages reach your inbox. Sign-in links and invitations are always sent.
          </p>
          {prefs.error && (
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">Your preferences could not be loaded.</div>
          )}
          {save.error && (
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
              {isCallFailure(save.error) ? save.error.message : 'Your preferences could not be saved.'}
            </div>
          )}
          {saved && !dirty && (
            <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">Saved.</div>
          )}
          <form onSubmit={submit} noValidate>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={!draft || save.isPending}>
              <legend className="vh">Email me when</legend>
              {visibleKinds.map((k) => (
                <label key={k} className={form.check}>
                  <input
                    type="checkbox"
                    checked={draft?.[k] ?? false}
                    onChange={(e) => { setSaved(false); setDraft((d) => (d ? { ...d, [k]: e.target.checked } : d)); }}
                  />
                  <span>
                    {MAIL_LABELS[k].label}
                    {MAIL_LABELS[k].hint && <span className={form.hint}>{MAIL_LABELS[k].hint}</span>}
                  </span>
                </label>
              ))}
            </fieldset>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={!dirty || save.isPending} style={{ marginTop: 'var(--sp-3)' }}>
              {save.isPending ? 'Saving…' : 'Save email preferences'}
            </button>
          </form>
        </section>

        {/* The organization's System Admin (or a StandingDocs system
            administrator signed into it) sees its single sign-on setup
            here; the second factor is asked for first, as in the console.

            WITHHELD while SSO_RELEASED_TO_CUSTOMERS is false: single sign-on
            has never carried a real sign-in, so a customer is not shown a
            panel that would imply they can have it. The panel itself, and
            every callable behind it, is untouched and still reached by a
            platform system administrator from the console. See
            `web/src/auth/sso.ts`. */}
        {SSO_RELEASED_TO_CUSTOMERS && roles.includes('sysadmin') && orgId && (
          <section className={styles.section} aria-labelledby="sso-settings-title">
            <h2 className={styles.sectionTitle} id="sso-settings-title">Single sign-on for this organization</h2>
            <p style={{ maxWidth: '68ch', lineHeight: 1.6, color: 'var(--ink-2)', marginTop: 0 }}>
              {session?.platformSysAdmin
                ? 'Visible to you because you are a StandingDocs system administrator.'
                : "You are this organization's System Admin, so this is yours to set up."}{' '}
              Other administrators can see the setup, not change it.
            </p>
            <MfaGate compact>
              <SsoSection orgId={orgId} />
            </MfaGate>
          </section>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Signing in</h2>
          <p style={{ maxWidth: '68ch', lineHeight: 1.6, color: 'var(--ink-2)' }}>
            This account has no password. You sign in with a link emailed to{' '}
            <strong>{session?.email ?? 'your address'}</strong>, which works
            once and expires after 15 minutes. If you stop receiving those
            links, ask an administrator to confirm your address is still active.
          </p>
          <button
            type="button"
            className={styles.btn}
            onClick={signOut}
            style={{ marginTop: 'var(--sp-4)' }}
          >
            Sign out
          </button>
        </section>
      </div>
    </>
  );
}
